import React, { useState, useEffect } from 'react';
import { Program, Quest } from '../types';
import { programService } from '../services/programService';

interface ProgramRoadmapModalProps {
  isOpen: boolean;
  program: Program | null;
  quests: Quest[];
  onClose: () => void;
}

const ProgramRoadmapModal: React.FC<ProgramRoadmapModalProps> = ({ isOpen, program, quests, onClose }) => {
  const [expandedPhase, setExpandedPhase] = React.useState<number | null>(null);

  if (!isOpen || !program) return null;

  // Helper function to get current phase for AI programs based on quest completion
  const getCurrentPhaseForDisplay = (program: Program, quests: Quest[]): number => {
    const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                       (!program.questTemplates || program.questTemplates.length === 0);

    if (!isAIProgram) {
      return programService.getCurrentPhase(program);
    }

    // For AI programs, determine current phase based on quest completion
    let currentPhase = 1;

    // Check each phase to see if it's completed (all scheduled quests done)
    for (let phase = 1; phase <= program.totalPhases; phase++) {
      const phaseQuests = quests.filter(quest => {
        if (!quest.programId || quest.programId !== program.id) return false;
        if (!quest.scheduledFor) return false;

        const questDate = new Date(quest.scheduledFor);
        const startDate = new Date(program.startDate);
        const dayOffset = Math.floor((questDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const questPhase = Math.floor(dayOffset / 7) + 1;

        return questPhase === phase;
      });

      // If this phase has quests and not all are completed, this is the current phase
      if (phaseQuests.length > 0) {
        const completedQuests = phaseQuests.filter(quest => quest.status === 'completed');
        if (completedQuests.length < phaseQuests.length) {
          currentPhase = phase;
          break;
        }
      } else {
        // If no quests exist for this phase yet, stop here
        break;
      }
    }

    return Math.min(currentPhase, program.totalPhases);
  };

  const currentPhase = getCurrentPhaseForDisplay(program, quests);
  const stats = programService.getProgramStats(program);

  return (
    <div className="fixed inset-0 backdrop-blur-xl flex items-center justify-center z-50 p-4 modal-backdrop animate-fade-in">
      <div className="relative max-w-4xl w-full animate-scale-in">
        {/* Main Modal */}
        <div className="relative bg-gradient-to-b from-slate-800/50 via-slate-900/50 to-slate-950/50 backdrop-blur-md border-t-2 border-l-2 border-purple-500/40 border-r-2 border-b-2 border-r-purple-700/60 border-b-purple-700/60 rounded-3xl shadow-2xl overflow-hidden"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {/* Header */}
          <div className="bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm border-b border-b-slate-500/30 p-6"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <h2 className="text-base sm:text-2xl font-black font-orbitron tracking-wider mb-0.5 sm:mb-1" style={{ color: 'var(--color-primary)' }}>
                  📍 {program.title}
                </h2>
                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs sm:text-sm font-rajdhani text-gray-400">
                  <span>Phase {currentPhase}/{program.totalPhases}</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{program.progress}% Complete</span>
                  <span className="hidden sm:inline">•</span>
                  <span>{programService.getDaysRemaining(program)} days remaining</span>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-all hover:rotate-90 duration-300 bg-gradient-to-b from-slate-700/50 to-slate-800/50 rounded-full p-2 border-t-2 border-l-2 border-slate-600/40 border-r-2 border-b-2 border-slate-700/60"
                style={{
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                }}
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Overall Progress Bar */}
            <div className="mt-4">
              <div className="w-full bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-full h-4 overflow-hidden transition-all duration-500"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div
                  className="h-full transition-all duration-500 relative"
                  style={{ 
                    width: `${program.progress}%`,
                    background: `linear-gradient(to right, var(--color-primary), var(--color-secondary), var(--color-primary))`
                  }}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-30 animate-pulse" />
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-3 sm:p-6 space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto">
            {/* Stats Overview */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-4 text-center"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1">Quests</div>
                <div className="text-2xl font-bold font-orbitron text-white">
                  {program.questsCompleted}
                </div>
                <div className="text-xs text-gray-500 font-rajdhani">
                  of {stats.totalQuestsExpected}
                </div>
              </div>
              <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-4 text-center"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1">Completion</div>
                <div className="text-2xl font-bold font-orbitron" style={{ color: 'var(--color-secondary)' }}>
                  {Math.round(stats.completionRate)}%
                </div>
                <div className="text-xs text-gray-500 font-rajdhani">
                  rate
                </div>
              </div>
              <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-4 text-center"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1">Days Active</div>
                <div className="text-2xl font-bold font-orbitron" style={{ color: 'var(--color-primary)' }}>
                  {stats.daysActive}
                </div>
                <div className="text-xs text-gray-500 font-rajdhani">
                  of {program.duration}
                </div>
              </div>
              <div className="bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl p-4 text-center"
                style={{
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                }}
              >
                <div className="text-xs text-gray-400 font-rajdhani uppercase mb-1">Milestones</div>
                <div className="text-2xl font-bold font-orbitron" style={{ color: 'var(--color-accent)' }}>
                  {stats.milestonesCompleted}
                </div>
                <div className="text-xs text-gray-500 font-rajdhani">
                  of {program.milestones.length}
                </div>
              </div>
            </div>

            {/* Milestones Roadmap */}
            <div>
              <h3 className="text-base sm:text-lg font-bold font-orbitron mb-4 flex items-center gap-2" style={{ color: 'var(--color-primary)' }}>
                <span>🗺️</span>
                <span>PROGRAM ROADMAP</span>
              </h3>

              <div className="space-y-4">
                {program.milestones.map((milestone, index) => {
                  const isCompleted = milestone.completed;

                  // Check if this is an AI program (has aiFollowUpAnswers or no questTemplates)
                  const isAIProgram = (program.aiFollowUpAnswers && Object.keys(program.aiFollowUpAnswers).length > 0) ||
                                    (!program.questTemplates || program.questTemplates.length === 0);

                  let isCurrent = false;
                  let isLocked = false;

                  if (isAIProgram) {
                    // For AI programs: use quest completion based logic
                    isCurrent = milestone.phase === currentPhase;
                    isLocked = milestone.phase > currentPhase;
                  } else {
                    // For traditional programs: use date-based logic
                    isCurrent = !isCompleted && milestone.phase === currentPhase;
                    isLocked = milestone.phase > currentPhase;
                  }


                  // For AI programs, get actual generated quests for this phase
                  // For traditional programs, show quest templates
                  let phaseQuests: any[] = [];
                  let showFuturePhaseMessage = false;

                  if (isAIProgram) {
                    // For AI programs, check if quests have been generated for this phase
                    // Get all quests for this program, sorted by creation date
                    const programQuests = quests
                      .filter(quest => quest.programId === program.id && quest.scheduledFor)
                      .sort((a, b) => new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime());

                    // AI programs generate quests in batches per phase
                    // Each phase generates N quests where N = availableDays.length
                    const questsPerPhase = program.availableDays ? program.availableDays.length : 7;
                    const phaseStartIndex = (milestone.phase - 1) * questsPerPhase;
                    const phaseEndIndex = phaseStartIndex + questsPerPhase;

                    // Get quests for this specific phase
                    const actualPhaseQuests = programQuests.slice(phaseStartIndex, phaseEndIndex);

                    // If we have actual generated quests for this phase, show them
                    if (actualPhaseQuests.length > 0) {
                      phaseQuests = actualPhaseQuests;
                    } else {
                      // Future phase - quests not generated yet
                      showFuturePhaseMessage = true;
                      // Calculate expected number of quests based on available days
                      const availableDaysCount = program.availableDays ? program.availableDays.length : 7;
                      const expectedQuests = Math.max(1, availableDaysCount); // At least 1 quest per phase
                      phaseQuests = Array(expectedQuests).fill(null).map((_, index) => ({
                        id: `future_${milestone.phase}_${index}`,
                        title: `Quest ${index + 1} - To be generated`,
                        description: `This quest will be personalized based on your progress and preferences when you reach Phase ${milestone.phase}.`,
                        type: 'generated',
                        difficulty: 'normal',
                        scheduledFor: null
                      }));
                    }
                  } else {
                    // Traditional program - show quest templates
                    phaseQuests = program.questTemplates.filter(qt => qt.phase === milestone.phase);
                  }

                  return (
                    <div
                      key={milestone.id}
                      className={`relative pl-8 pb-4 ${
                        index < program.milestones.length - 1 ? 'border-l-2' : ''
                      } ${
                        isCompleted
                          ? 'border-green-500/50'
                          : isCurrent
                          ? 'border-system-purple'
                          : 'border-gray-700'
                      }`}
                    >
                      {/* Timeline Dot */}
                      <div
                        className={`absolute left-0 top-0 w-6 h-6 rounded-full border-2 flex items-center justify-center transform -translate-x-[13px] ${
                          isCompleted
                            ? 'shadow-lg animate-pulse'
                            : isCurrent
                            ? 'shadow-lg animate-pulse'
                            : ''
                        }`}
                        style={isCompleted ? {
                          background: 'var(--color-primary)',
                          borderColor: 'var(--color-primary)',
                          boxShadow: `0 0 15px color-mix(in srgb, var(--color-primary) 50%, transparent)`
                        } : isCurrent ? {
                          background: 'var(--color-primary)',
                          borderColor: 'var(--color-primary)',
                          boxShadow: `0 0 15px color-mix(in srgb, var(--color-primary) 50%, transparent)`
                        } : {
                          background: 'color-mix(in srgb, var(--color-background) 80%, rgba(51,65,85,0.8))',
                          borderColor: 'color-mix(in srgb, var(--color-border) 40%, rgba(100,116,139,0.4))'
                        }}
                      >
                        {isCompleted ? (
                          <span className="text-white text-xs">✓</span>
                        ) : isCurrent ? (
                          <span className="text-white text-xs">⏳</span>
                        ) : (
                          <span className="text-gray-500 text-xs">🔒</span>
                        )}
                      </div>

                      {/* Milestone Card */}
                      <button
                        onClick={() => setExpandedPhase(expandedPhase === milestone.phase ? null : milestone.phase)}
                        className={`w-full rounded-2xl border-t-2 border-l-2 border-r-2 border-b-2 overflow-hidden transition-all backdrop-blur-sm hover:scale-105 ${
                          isCurrent ? 'opacity-100' : 'opacity-90'
                        }`}
                        style={isCompleted ? {
                          background: 'linear-gradient(to bottom, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.08))',
                          borderColor: 'rgba(34, 197, 94, 0.4)',
                          borderRightColor: 'rgba(34, 197, 94, 0.7)',
                          borderBottomColor: 'rgba(34, 197, 94, 0.7)',
                          boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                        } : isCurrent ? {
                          background: 'linear-gradient(to bottom, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.08))',
                          borderColor: 'rgba(59, 130, 246, 0.4)',
                          borderRightColor: 'rgba(59, 130, 246, 0.7)',
                          borderBottomColor: 'rgba(59, 130, 246, 0.7)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1)'
                        } : {
                          background: 'linear-gradient(to bottom, rgba(51, 65, 85, 0.5), rgba(71, 85, 105, 0.4))',
                          borderColor: 'rgba(100, 116, 139, 0.4)',
                          borderRightColor: 'rgba(71, 85, 105, 0.6)',
                          borderBottomColor: 'rgba(71, 85, 105, 0.6)',
                          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05)'
                        }}
                      >
                        <div className="p-3 sm:p-4 text-left">
                          {/* Header */}
                          <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-1.5 sm:gap-2 mb-0.5 sm:mb-1 flex-wrap">
                                <span
                                  className="text-xs font-bold font-orbitron px-2 py-1 rounded border"
                                  style={isCurrent ? {
                                    background: 'color-mix(in srgb, var(--color-primary) 20%, rgba(59,130,246,0.2))',
                                    color: 'var(--color-primary)',
                                    borderColor: 'color-mix(in srgb, var(--color-primary) 30%, rgba(59,130,246,0.3))'
                                  } : {
                                    background: 'color-mix(in srgb, var(--color-background) 50%, rgba(51,65,85,0.5))',
                                    color: 'var(--color-text-secondary)',
                                    borderColor: 'color-mix(in srgb, var(--color-border) 50%, rgba(100,116,139,0.5))'
                                  }}
                                >
                                  PHASE {milestone.phase}
                                </span>
                                {isCompleted && (
                                  <span className="text-xs font-rajdhani" style={{ color: 'var(--color-accent)' }}>
                                    ✅ COMPLETED
                                  </span>
                                )}
                                {isCurrent && (
                                  <span className="text-xs font-rajdhani animate-pulse" style={{ color: 'var(--color-primary)' }}>
                                    📍 CURRENT PHASE
                                  </span>
                                )}
                              </div>
                              <h4 className="font-orbitron font-bold text-white text-sm sm:text-base mb-0.5 sm:mb-1">
                                {milestone.title}
                              </h4>
                              <p className="text-xs sm:text-sm text-gray-400 font-rajdhani">
                                {milestone.description}
                              </p>
                            </div>
                            <div className={`transform transition-transform ml-2 ${expandedPhase === milestone.phase ? 'rotate-180' : ''}`}>
                              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                          </div>

                          {/* Quest Count */}
                          <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-[10px] sm:text-xs font-rajdhani text-gray-400 mb-2 sm:mb-3">
                            <span>🎯 {phaseQuests.length} {isAIProgram ? 'generated quests' : 'quest templates'}</span>
                            <span className="hidden sm:inline">•</span>
                            <span>📅 Target: {new Date(milestone.targetDate).toLocaleDateString()}</span>
                          </div>

                          {/* Rewards */}
                          <div
                            className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 text-xs font-rajdhani p-3 rounded-xl bg-gradient-to-b from-amber-900/30 via-yellow-950/20 to-amber-900/30 backdrop-blur-sm border-t-2 border-l-2 border-amber-500/40 border-r-2 border-b-2 border-r-amber-700/60 border-b-amber-700/60"
                            style={{
                              boxShadow: '0 4px 12px rgba(251, 191, 36, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.1), inset 0 -2px 4px rgba(0, 0, 0, 0.4)'
                            }}
                          >
                            <span className="text-gray-400">🏆 Rewards:</span>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="text-blue-400">+{milestone.reward.xp} XP</span>
                              <span className="text-yellow-400">+💰{milestone.reward.currency}</span>
                              <span className="text-purple-400">+{milestone.reward.statPoints} Stat Points</span>
                            </div>
                          </div>

                          {/* Expanded Quest List */}
                          {expandedPhase === milestone.phase && phaseQuests.length > 0 && (
                            <div className="mt-4 pt-4 border-t space-y-2" style={{
                              borderColor: 'color-mix(in srgb, var(--color-border) 30%, rgba(100,116,139,0.3))'
                            }}>
                              <div className="font-orbitron text-xs font-bold uppercase mb-2" style={{ color: 'var(--color-primary)' }}>
                                📋 {isAIProgram
                                  ? showFuturePhaseMessage
                                    ? 'Future Quests - To be Generated:'
                                    : 'Generated Quests for this Week:'
                                  : 'Quest Templates for this Week:'
                                }
                              </div>
                              {phaseQuests.map((quest, qIndex) => (
                                <div
                                  key={quest.id || qIndex}
                                  className={`backdrop-blur-sm border-t-2 border-l-2 border-r-2 border-b-2 rounded-2xl p-3 transition-all ${
                                    showFuturePhaseMessage
                                      ? 'bg-gradient-to-b from-slate-700/30 via-slate-800/20 to-slate-900/30 border-slate-600/30 border-r-slate-700/40 border-b-slate-700/40'
                                      : 'bg-gradient-to-b from-slate-800/50 via-slate-900/40 to-slate-950/50 border-slate-500/40 border-r-slate-700/60 border-b-slate-700/60 hover:border-blue-500/50 hover:scale-105'
                                  }`}
                                  style={{
                                    boxShadow: showFuturePhaseMessage
                                      ? '0 2px 8px rgba(0, 0, 0, 0.2), inset 0 1px 2px rgba(255, 255, 255, 0.03), inset 0 -2px 4px rgba(0, 0, 0, 0.2)'
                                      : '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
                                  }}
                                >
                                  <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1">
                                      <div className={`font-orbitron font-bold text-sm ${showFuturePhaseMessage ? 'text-gray-300' : 'text-white'}`}>
                                        {showFuturePhaseMessage && <span className="text-yellow-400 mr-1">⏳</span>}
                                        {quest.title}
                                      </div>
                                      {quest.description && (
                                        <div className={`text-xs font-rajdhani mt-1 ${showFuturePhaseMessage ? 'text-gray-500' : 'text-gray-400'}`}>
                                          {quest.description}
                                        </div>
                                      )}
                                      <div className="flex items-center gap-2 mt-2">
                                        <span className={`text-xs px-2 py-0.5 rounded font-rajdhani font-bold ${
                                          showFuturePhaseMessage
                                            ? 'bg-gray-600/30 text-gray-400'
                                            : quest.difficulty === 'easy' ? 'bg-green-500/20 text-green-400' :
                                              quest.difficulty === 'normal' ? 'bg-blue-500/20 text-blue-400' :
                                              quest.difficulty === 'hard' ? 'bg-orange-500/20 text-orange-400' :
                                              'bg-red-500/20 text-red-400'
                                        }`}>
                                          {showFuturePhaseMessage ? 'pending' : quest.difficulty}
                                        </span>
                                        <span className="text-xs text-gray-500">•</span>
                                        <span className={`text-xs font-rajdhani ${showFuturePhaseMessage ? 'text-gray-500' : 'text-gray-400'}`}>
                                          {showFuturePhaseMessage ? 'AI-generated' : quest.type}
                                        </span>
                                        {isAIProgram && quest.scheduledFor && !showFuturePhaseMessage && (
                                          <>
                                            <span className="text-xs text-gray-500">•</span>
                                            <span className="text-xs text-gray-400 font-rajdhani">
                                              📅 {new Date(quest.scheduledFor).toLocaleDateString()}
                                            </span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ))}
                              <div className="text-xs text-gray-500 font-rajdhani italic mt-2">
                                {isAIProgram
                                  ? showFuturePhaseMessage
                                    ? `💡 These quests will be generated when you reach Phase ${milestone.phase}. They'll be personalized based on your progress and preferences.`
                                    : "💡 These are the actual quests generated for this week based on your preferences and schedule."
                                  : "💡 These quests will be generated automatically based on your schedule."
                                }
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-t-slate-500/30 p-6 bg-gradient-to-b from-slate-800/60 via-slate-900/50 to-slate-950/60 backdrop-blur-sm"
            style={{
              boxShadow: 'inset 0 2px 4px rgba(255, 255, 255, 0.05)'
            }}
          >
            <button
              onClick={onClose}
              className="w-full py-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/40 border-r-2 border-b-2 border-r-slate-700/60 border-b-slate-700/60 rounded-2xl font-orbitron font-bold text-base tracking-wider transition-all text-white hover:scale-105 relative"
              style={{
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3), inset 0 1px 2px rgba(255, 255, 255, 0.05), inset 0 -2px 4px rgba(0, 0, 0, 0.3)'
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-black/10 rounded-2xl"></div>
              <span className="relative z-10">CLOSE</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProgramRoadmapModal;


